import { useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { GripVertical, Eye, EyeOff, Trash2, ChevronDown, Plus } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, SwitchRow, IconUpload } from "@/components/editor/fields";
import { LINK_ICONS, LinkIcon } from "@/components/card/icons";
import { newId } from "@/lib/cardUtils";
import { fileUrl } from "@/lib/api";

export const DragRow = ({ value, title, subtitle, icon, visible, onToggle, onDelete, children, testId, defaultOpen = false }) => {
  const controls = useDragControls();
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Reorder.Item value={value} dragListener={false} dragControls={controls} className="rounded-lg border bg-white" data-testid={testId}>
      <div className="flex items-center gap-2 px-2 py-2">
        <button type="button" onPointerDown={(e) => controls.start(e)} className="cursor-grab touch-none p-1 text-zinc-400 hover:text-zinc-700 active:cursor-grabbing" data-testid={`${testId}-drag`} aria-label="Trascina">
          <GripVertical className="h-4 w-4" />
        </button>
        {icon}
        <button type="button" className="min-w-0 flex-1 text-left" onClick={() => setOpen(!open)} data-testid={`${testId}-toggle-open`}>
          <div className={`truncate text-sm ${visible ? "text-zinc-900" : "text-zinc-400 line-through"}`}>{title}</div>
          {subtitle && <div className="truncate text-[11px] text-zinc-400">{subtitle}</div>}
        </button>
        <button type="button" onClick={onToggle} className="p-1.5 text-zinc-400 hover:text-zinc-800" data-testid={`${testId}-visibility`} title={visible ? "Nascondi" : "Mostra"}>
          {visible ? <Eye className="h-4 w-4" strokeWidth={1.6} /> : <EyeOff className="h-4 w-4" strokeWidth={1.6} />}
        </button>
        {onDelete && <button type="button" onClick={onDelete} className="p-1.5 text-zinc-400 hover:text-red-600" data-testid={`${testId}-delete`} title="Elimina"><Trash2 className="h-4 w-4" strokeWidth={1.6} /></button>}
        {children && <ChevronDown className={`h-4 w-4 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} onClick={() => setOpen(!open)} />}
      </div>
      {open && children && <div className="space-y-3 border-t bg-zinc-50/60 p-3">{children}</div>}
    </Reorder.Item>
  );
};

const PRESETS = [
  { title: "Prenota appuntamento", icon: "calendar" },
  { title: "Visita il sito", icon: "globe" },
  { title: "Richiedi informazioni", icon: "mail" },
];

export const LinksPanel = ({ card, update }) => {
  const links = card.links;
  const [added, setAdded] = useState(null);
  const add = (p = {}) => {
    const id = newId();
    setAdded(id);
    update("links", [...links, { id, title: p.title || "Nuovo link", url: "", icon: p.icon || "link", icon_url: "", description: "", visible: true, is_cta: !!p.title }]);
  };
  const setLink = (idx, k, v) => update(`links.${idx}.${k}`, v);
  return (
    <div className="space-y-3" data-testid="editor-links-section">
      <Reorder.Group axis="y" values={links} onReorder={(v) => update("links", v)} className="space-y-2">
        {links.map((l, idx) => (
          <DragRow key={l.id} value={l} defaultOpen={l.id === added} testId={`link-item-${idx}`} title={l.title || "Senza titolo"} subtitle={l.url || "URL mancante"}
            icon={l.icon_url ? <img src={fileUrl(l.icon_url)} alt="" className="h-4 w-4 object-contain" /> : <LinkIcon name={l.icon} className="h-4 w-4 text-zinc-500" />} visible={l.visible}
            onToggle={() => setLink(idx, "visible", !l.visible)} onDelete={() => update("links", links.filter((x) => x.id !== l.id))}>
            <Field label="Titolo" value={l.title} onChange={(v) => setLink(idx, "title", v)} testId={`link-title-${idx}`} />
            <Field label="URL" value={l.url} onChange={(v) => setLink(idx, "url", v)} testId={`link-url-${idx}`} placeholder="https://, mailto:, tel:" />
            <Field label="Testo descrittivo" value={l.description} onChange={(v) => setLink(idx, "description", v)} testId={`link-description-${idx}`} />
            <div>
              <div className="mb-1.5 text-xs text-zinc-600">Icona (standard o tua immagine)</div>
              <div className="flex items-center gap-2">
              <div className="flex-1"><Select value={l.icon} onValueChange={(v) => setLink(idx, "icon", v)}>
                <SelectTrigger className="bg-white" data-testid={`link-icon-${idx}`}><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(LINK_ICONS).map(([k, v]) => <SelectItem key={k} value={k}><span className="flex items-center gap-2"><v.Icon className="h-4 w-4" />{v.label}</span></SelectItem>)}</SelectContent>
              </Select></div>
              <IconUpload value={l.icon_url} onChange={(v) => setLink(idx, "icon_url", v)} testId={`link-icon-upload-${idx}`} />
              </div>
            </div>
            <SwitchRow label="Evidenzia come CTA" hint="Pulsante pieno nel colore principale" checked={l.is_cta} onChange={(v) => setLink(idx, "is_cta", v)} testId={`link-cta-${idx}`} />
          </DragRow>
        ))}
      </Reorder.Group>
      <button type="button" onClick={() => add()} data-testid="add-link-button" className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed py-2.5 text-sm text-zinc-600 transition-colors hover:border-zinc-500 hover:text-zinc-900">
        <Plus className="h-4 w-4" /> Aggiungi link
      </button>
      <div className="flex flex-wrap gap-1.5">
        {PRESETS.map((p) => (
          <button key={p.title} type="button" onClick={() => add(p)} data-testid={`add-cta-${p.icon}`} className="rounded-full border bg-white px-3 py-1 text-xs text-zinc-600 hover:border-terra hover:text-terra">+ {p.title}</button>
        ))}
      </div>
    </div>
  );
};
